import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, GBP, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Calendar, Clock, User, CheckCircle2, ArrowRight, Award, ShieldCheck, PlayCircle, Share2, Link2, Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CourseCard } from "@/components/CourseCard";
import { Seo } from "@/components/Seo";

function ShareRow({ course }) {
  const [copied, setCopied] = useState(false);
  const slug = course.slug || course.id;
  const shareUrl = `${API}/share/courses/${slug}`;
  const text = `${course.title} — Get Skilled in Days Not Years`;
  const u = encodeURIComponent(shareUrl);
  const t = encodeURIComponent(text);
  const links = [
    { name: "WhatsApp", testid: "share-whatsapp", href: `https://wa.me/?text=${t}%20${u}` },
    { name: "Facebook", testid: "share-facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
    { name: "LinkedIn", testid: "share-linkedin", href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
    { name: "X", testid: "share-x", href: `https://twitter.com/intent/tweet?url=${u}&text=${t}` },
  ];
  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch (e) { /* clipboard blocked */ }
  };
  return (
    <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="share-row">
      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--ink-2)]"><Share2 size={15} /> Share</span>
      {links.map((l) => (
        <a key={l.name} href={l.href} target="_blank" rel="noreferrer" data-testid={l.testid}
          className="text-xs font-semibold border border-[var(--line)] px-3 py-1.5 hover:border-[var(--ink)] hover:bg-[var(--alt)] transition-colors">{l.name}</a>
      ))}
      <button onClick={copy} data-testid="share-copy"
        className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[var(--line)] px-3 py-1.5 hover:border-[var(--ink)] hover:bg-[var(--alt)] transition-colors">
        {copied ? <><Check size={13} className="text-[#1B5E20]" /> Copied</> : <><Link2 size={13} /> Copy link</>}
      </button>
    </div>
  );
}

function embedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
}

export default function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [related, setRelated] = useState([]);
  const [heroImg, setHeroImg] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/courses/${id}`).then((r) => {
      setCourse(r.data);
      document.title = `${r.data.title} — Get Skilled in Days Not Years`;
      if (r.data.slug && r.data.slug !== id) navigate(`/courses/${r.data.slug}`, { replace: true });
    }).catch(() => setCourse(false));
    api.get(`/courses/${id}/related`).then((r) => setRelated(r.data)).catch(() => setRelated([]));
  }, [id, navigate]);

  if (course === null) return <div className="py-32 text-center text-[var(--ink-2)]">Loading…</div>;
  if (course === false) return <div className="py-32 text-center">Course not found. <Link to="/courses" className="text-[var(--accent)]">Back to catalogue</Link></div>;

  const full = course.places_remaining <= 0;
  const video = embedUrl(course.video_url);
  const apply = () => {
    if (!user) navigate("/login", { state: { from: `/apply/${course.id}` } });
    else navigate(`/apply/${course.id}`);
  };

  const Section = ({ title, children }) => (
    <div className="py-6 border-b border-[var(--line)]">
      <h3 className="font-display font-bold text-xl mb-3">{title}</h3>
      {children}
    </div>
  );

  return (
    <div data-testid="course-detail-page">
      <Seo
        title={course.title}
        description={course.summary || `${course.title} — a practical, CPD-accredited online course.`}
        path={`/courses/${course.slug || course.id}`}
        image={course.image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Course",
          name: course.title,
          description: course.summary || course.title,
          image: course.image,
          provider: { "@type": "Organization", name: "Get Skilled in Days Not Years", sameAs: "https://www.virtualspeedyservices.uk" },
          offers: { "@type": "Offer", price: course.price, priceCurrency: "GBP", availability: full ? "https://schema.org/SoldOut" : "https://schema.org/InStock" },
          hasCourseInstance: { "@type": "CourseInstance", courseMode: "online", courseWorkload: course.duration || "P4W" },
        }}
      />
      <div className="relative border-b border-[var(--line)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-12 gap-8 py-10">
          <div className="lg:col-span-7">
            <div className="border border-[var(--ink)] mb-6">
              {video ? (
                <div className="relative w-full aspect-[16/9]" data-testid="course-video">
                  <iframe src={video} title="Course video" className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              ) : (
                <img src={heroImg || course.image} alt={course.title} className="w-full aspect-[16/9] object-cover" />
              )}
            </div>
            {(course.gallery || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6" data-testid="course-gallery">
                {[course.image, ...(course.gallery || [])].map((url, i) => (
                  <button key={url + i} type="button" data-testid={`gallery-thumb-${i}`} onClick={() => setHeroImg(url)}
                    className={`w-20 h-20 overflow-hidden border ${(heroImg || course.image) === url ? "border-[var(--accent)]" : "border-[var(--line)] hover:border-[var(--ink)]"} transition-colors`}>
                    <img src={url} alt={`${course.title} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <span className="text-xs tracking-label uppercase font-semibold text-[var(--accent)]">{course.category} · {course.level}</span>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight mt-2">{course.title}</h1>
            <p className="mt-4 text-lg text-[var(--ink-2)]">{course.summary}</p>
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[var(--ink-2)]">
              <span className="inline-flex items-center gap-1.5"><MapPin size={15} /> {course.location}</span>
              <span className="inline-flex items-center gap-1.5"><Calendar size={15} /> Starts {course.start_date}</span>
              <span className="inline-flex items-center gap-1.5"><Clock size={15} /> {course.duration}</span>
              <span className="inline-flex items-center gap-1.5"><User size={15} /> {course.delivery}</span>
            </div>
            <ShareRow course={course} />
          </div>

          {/* Sticky purchase card */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24 border border-[var(--ink)] bg-white p-6" data-testid="enrol-box">
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wide text-[var(--ink-2)]">Full price</div>
                  <div className="font-display font-extrabold text-4xl">{GBP(course.price)}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-wide text-[var(--ink-2)]">Deposit</div>
                  <div className="font-display font-bold text-xl text-[var(--accent)]">{GBP(course.deposit_amount)}</div>
                </div>
              </div>

              <div className="mt-5 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[var(--ink-2)]">Places remaining</span><span className="font-semibold">{course.places_remaining} / {course.capacity}</span></div>
                <div className="flex items-center gap-2 text-[var(--ink-2)]"><CheckCircle2 size={15} className="text-[var(--accent)]" /> Complete course in 4 weeks</div>
                <div className="flex items-center gap-2 text-[var(--ink-2)]"><CheckCircle2 size={15} className="text-[var(--accent)]" /> CPD Certificate of Completion</div>
              </div>

              <button
                onClick={apply}
                data-testid="apply-now-button"
                className={`mt-6 w-full font-semibold px-6 py-4 inline-flex items-center justify-center gap-2 transition-colors ${full ? "bg-[var(--alt)] text-[var(--ink-2)]" : "bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)]"}`}
              >
                {full ? "Join the waiting list" : "Apply now · Reserve my place"} <ArrowRight size={18} />
              </button>
              <p className="mt-3 text-xs text-[var(--ink-2)] text-center inline-flex items-center gap-1.5 w-full justify-center"><ShieldCheck size={13} /> Secure checkout · Pay deposit or in full</p>
            </div>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
        <Section title="Who this course is for"><p className="text-[var(--ink-2)]">{course.who_for}</p></Section>
        <Section title="Skills you'll gain">
          <ul className="grid sm:grid-cols-2 gap-2">
            {course.skills?.map((s) => <li key={s} className="flex items-start gap-2 text-sm"><CheckCircle2 size={16} className="text-[var(--accent)] mt-0.5 shrink-0" /> {s}</li>)}
          </ul>
        </Section>
        <Section title="Course outcomes">
          <ul className="space-y-2">{course.outcomes?.map((s) => <li key={s} className="flex items-start gap-2 text-sm"><ArrowRight size={16} className="text-[var(--accent)] mt-0.5 shrink-0" /> {s}</li>)}</ul>
        </Section>
        <Section title="Timetable">
          <div className="space-y-2">
            {course.timetable?.map((t, i) => (
              <div key={i} className="flex items-center justify-between border border-[var(--line)] px-4 py-3 text-sm">
                <span className="font-semibold">{t.label}</span><span className="text-[var(--ink-2)]">{t.date} · {t.time}</span>
              </div>
            ))}
          </div>
        </Section>
        <Section title="What's included"><ul className="grid sm:grid-cols-2 gap-2">{course.included?.map((s) => <li key={s} className="flex items-start gap-2 text-sm"><CheckCircle2 size={16} className="text-[var(--accent)] mt-0.5 shrink-0" /> {s}</li>)}</ul></Section>
        <Section title="Entry requirements"><ul className="space-y-1.5 text-sm text-[var(--ink-2)] list-disc pl-5">{course.requirements?.map((s) => <li key={s}>{s}</li>)}</ul></Section>
        <Section title="Your tutor">
          <div className="flex items-start gap-4">
            <span className="grid place-items-center h-12 w-12 bg-[var(--ink)] text-white shrink-0"><User size={22} /></span>
            <div><div className="font-semibold">{course.tutor_name}</div><p className="text-sm text-[var(--ink-2)]">{course.tutor_bio}</p></div>
          </div>
        </Section>
        <Section title="Certificate">
          <div className="flex items-start gap-3"><Award size={20} className="text-[var(--accent)] mt-0.5" /><p className="text-sm text-[var(--ink-2)]">{course.certificate_details}</p></div>
        </Section>
        {course.faqs?.length > 0 && (
          <Section title="Frequently asked questions">
            <Accordion type="single" collapsible className="w-full">
              {course.faqs.map((f, i) => (
                <AccordionItem key={i} value={`f${i}`} className="border-[var(--line)]">
                  <AccordionTrigger className="text-left text-sm font-semibold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-[var(--ink-2)]">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Section>
        )}
        <Section title="Refund & cancellation policy"><p className="text-sm text-[var(--ink-2)]">{course.refund_policy}</p></Section>

        <button onClick={apply} data-testid="apply-now-bottom" className="mt-6 w-full bg-[var(--ink)] text-white font-semibold px-6 py-4 hover:bg-[var(--accent)] transition-colors inline-flex items-center justify-center gap-2">
          {full ? "Join the waiting list" : "Apply now"} <ArrowRight size={18} />
        </button>
      </div>

      {related.length > 0 && (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 border-t border-[var(--line)]" data-testid="recommended-courses">
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight mb-6">You might also like</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {related.map((c) => <CourseCard key={c.id} course={c} />)}
          </div>
        </div>
      )}
    </div>
  );
}
